import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Brain, Gauge, RotateCcw, XCircle, CheckCircle2, ShieldAlert, Clock, Tag, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { Card, CardHeader, Stat, EmptyState } from "@/components/common";

const TYPE_LABEL = {
  pricing: "Pricing", schedule: "Scheduling", design: "Design", structural: "Structural",
  interior: "Interior", subcontractor: "Subcontractor", cabinet: "Cabinet", digital: "Digital", general: "General",
};

export default function OwnerMind() {
  const { data: p, isLoading } = useQuery({
    queryKey: ["owner-profile"],
    queryFn: async () => (await api.get("/intelligence/owner-profile")).data,
  });
  if (isLoading || !p) return <div className="text-sm text-ink-muted">Loading…</div>;

  const byType = Object.entries(p.by_type || {}).filter(([, v]) => v.count > 0);

  return (
    <div data-testid="owner-mind-page">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.12em] uppercase text-ink-muted">
          <Brain className="w-3.5 h-3.5 text-brand" /> Owner Mind
        </div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-ink mt-1">How you think — learned over time.</h1>
        <p className="text-sm text-ink-muted mt-1">Every decision you make trains this model. The goal: a system that decides the way you would.</p>
      </div>

      {/* Narrative */}
      <div className="bg-ink text-white p-5 mb-4" data-testid="owner-narrative">
        <div className="flex items-center gap-2 mb-3"><Sparkles className="w-4 h-4" style={{ color: "#7CA0FF" }} /><span className="font-heading text-sm font-semibold uppercase tracking-wide">Your Decision Profile</span></div>
        <ul className="space-y-2">
          {p.narrative.map((n, i) => <li key={i} className="text-[15px] leading-relaxed text-white/90 flex items-start gap-2"><span className="text-brand" style={{ color: "#7CA0FF" }}>—</span> {n}</li>)}
        </ul>
      </div>

      {p.llm_narrative?.length > 0 && (
        <div className="bg-white border border-brand p-5 mb-4" data-testid="owner-narrative-llm">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-brand" />
            <span className="font-heading text-sm font-semibold uppercase tracking-wide text-ink">Owner-Mind Read</span>
            <span className="text-[10px] font-semibold uppercase tracking-wider border border-brand text-brand px-1.5 py-0.5">Claude Sonnet 4.6</span>
          </div>
          <ul className="space-y-2">
            {p.llm_narrative.map((n, i) => <li key={i} className="text-[15px] leading-relaxed text-ink-muted flex items-start gap-2"><span className="text-brand">—</span> {n}</li>)}
          </ul>
        </div>
      )}

      {p.decisions_total === 0 ? (
        <EmptyState icon={Brain} title="No decisions recorded yet" hint="Approve, revise or reject deliverables and your patterns will appear here." />
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
            <Stat testId="om-decisions" label="Decisions Made" value={p.decisions_total} icon={Gauge} />
            <Stat testId="om-approve" label="Approve Rate" value={`${p.approve_rate}%`} accent="text-st-success" icon={CheckCircle2} />
            <Stat testId="om-revise" label="Revise Rate" value={`${p.revise_rate}%`} accent="text-st-warning" icon={RotateCcw} />
            <Stat testId="om-reject" label="Reject Rate" value={`${p.reject_rate}%`} accent="text-st-critical" icon={XCircle} />
            <Stat testId="om-speed" label="Avg Decision" value={`${p.avg_decision_minutes}m`} icon={Clock} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* By type */}
            <Card className="lg:col-span-2" testId="om-by-type">
              <CardHeader title="How You Decide By Work Type" icon={Tag} />
              <div className="p-4 space-y-3">
                {byType.map(([t, v]) => {
                  const a = Math.round((v.approve / v.count) * 100);
                  const r = Math.round((v.revise / v.count) * 100);
                  const x = 100 - a - r;
                  return (
                    <div key={t}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold text-ink">{TYPE_LABEL[t] || t}</span>
                        <span className="text-[11px] font-mono text-ink-faint">{v.count} decision(s)</span>
                      </div>
                      <div className="flex h-4 w-full overflow-hidden border border-border">
                        <div className="bg-st-success" style={{ width: `${a}%` }} title={`Approve ${a}%`} />
                        <div className="bg-st-warning" style={{ width: `${r}%` }} title={`Revise ${r}%`} />
                        <div className="bg-st-critical" style={{ width: `${x}%` }} title={`Reject ${x}%`} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="px-4 pb-3 flex items-center gap-4 text-[11px] uppercase tracking-wider font-semibold text-ink-muted">
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-st-success" /> Approve</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-st-warning" /> Revise</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-st-critical" /> Reject</span>
              </div>
            </Card>

            <div className="space-y-4">
              {/* Risk tolerance */}
              <Card testId="om-risk">
                <CardHeader title="Risk Tolerance" icon={ShieldAlert} />
                <div className="p-4">
                  {p.risk_tolerance == null ? (
                    <p className="text-sm text-ink-muted">No risk-flagged decisions yet.</p>
                  ) : (
                    <>
                      <div className="font-heading text-4xl font-bold text-ink">{p.risk_tolerance}%</div>
                      <p className="text-sm text-ink-muted mt-1">of packages with open risks you approve as-is.</p>
                      <div className="mt-3 h-2 bg-surface-muted"><div className={`h-full ${p.risk_tolerance >= 60 ? "bg-st-warning" : "bg-st-success"}`} style={{ width: `${p.risk_tolerance}%` }} /></div>
                      <p className="text-xs text-ink-faint mt-2">{p.risk_tolerance >= 60 ? "You lean toward momentum over caution." : "You're cautious — risks usually mean revisions."}</p>
                    </>
                  )}
                </div>
              </Card>

              {/* Revision themes */}
              <Card testId="om-themes">
                <CardHeader title="Why You Send Work Back" icon={RotateCcw} />
                <div className="divide-y divide-border">
                  {p.top_revision_themes.length === 0 ? (
                    <div className="px-4 py-4 text-sm text-ink-muted">No revision themes yet.</div>
                  ) : p.top_revision_themes.map((t) => (
                    <div key={t.theme} className="flex items-center justify-between px-4 py-2.5">
                      <span className="text-sm text-ink capitalize">{t.theme}</span>
                      <span className="text-xs font-mono font-semibold text-ink-faint">{t.count}×</span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
